import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar } from "../components/Navbar";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Briefcase,
  Users,
  Video,
  Link2,
  CreditCard,
  TrendingUp,
  ArrowRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  Plus,
  Radio,
  PlayCircle,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [userRes, kpiRes, jobsRes, reportsRes] = await Promise.all([
          fetch(`${API}/api/auth/me`, { credentials: "include" }),
          fetch(`${API}/api/kpi`, { credentials: "include" }),
          fetch(`${API}/api/jobs`, { credentials: "include" }),
          fetch(`${API}/api/reports`, { credentials: "include" }),
        ]);

        if (userRes.ok) setUser(await userRes.json());
        if (kpiRes.ok) setKpi(await kpiRes.json());
        if (jobsRes.ok) setJobs(await jobsRes.json());
        if (reportsRes.ok) {
          const reports = await reportsRes.json();
          setRecentReports(Array.isArray(reports) ? reports.slice(0, 5) : []);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const COLORS = ["#10b981", "#ef4444", "#f59e0b"];

  const pieData = kpi ? [
    { name: "GO", value: kpi.go_count || 0 },
    { name: "NO GO", value: kpi.no_go_count || 0 },
    { name: t("reports.evaluate"), value: (kpi.completed_interviews || 0) - (kpi.go_count || 0) - (kpi.no_go_count || 0) },
  ].filter(d => d.value > 0) : [];

  const jobChartData = jobs.slice(0, 6).map(j => ({
    name: j.title?.length > 14 ? j.title.slice(0, 14) + "..." : j.title,
    count: j.candidates_count || 0,
  }));

  const candidateStatusData = kpi?.candidates_by_status ? Object.entries(kpi.candidates_by_status).map(([status, count]) => ({
    name: t(`candidates.status.${status}`) || status,
    value: count,
  })) : [];

  const StatCard = ({ title, value, subtitle, icon: Icon, color, onClick }) => (
    <Card className="card-flat hover:shadow-md transition-shadow cursor-pointer group" onClick={onClick} data-testid={`stat-${title}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className={`p-2.5 rounded-xl ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
          {onClick && <ArrowRight className="w-4 h-4 text-ink-muted dark:text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity" />}
        </div>
        <p className="text-xs text-ink-muted dark:text-zinc-500 font-medium uppercase tracking-wider">{title}</p>
        <h3 className="text-2xl font-bold text-ink dark:text-zinc-100 mt-1">{value}</h3>
        {subtitle && <p className="text-xs text-ink-muted dark:text-zinc-500 mt-0.5">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  const tooltipStyle = { backgroundColor: 'var(--canvas-default, #fff)', border: '1px solid var(--border, #e4e4e7)', borderRadius: '8px', fontSize: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' };

  return (
    <div className="min-h-screen bg-surface-secondary dark:bg-zinc-950">
      <Navbar user={user} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-ink dark:text-zinc-100">
              {t('dashboard.welcome')}{user?.name ? `, ${user.name}` : ''}
            </h1>
            <p className="text-ink-muted dark:text-zinc-400 text-sm mt-1">{t('dashboard.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-3 py-1.5 border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold">
              <CreditCard className="w-3.5 h-3.5 mr-1.5" />
              {kpi?.interview_credits ?? user?.interview_credits ?? 0} {t('dashboard.credits')}
            </Badge>
            <Button className="btn-primary" onClick={() => navigate("/jobs")}>
              <Plus className="w-4 h-4 mr-2" />{t('dashboard.newInterview')}
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <StatCard title={t('dashboard.stats.jobs')} value={kpi?.total_jobs ?? jobs.length} icon={Briefcase} color="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400" onClick={() => navigate("/jobs")} />
          <StatCard title={t('dashboard.stats.candidates')} value={kpi?.total_candidates ?? 0} icon={Users} color="bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400" onClick={() => navigate("/candidates")} />
          <StatCard title={t('dashboard.stats.interviews')} value={kpi?.total_interviews ?? 0} icon={Video} color="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400" onClick={() => navigate("/reports")} />
          <StatCard title={t('dashboard.stats.completed')} value={kpi?.completed_interviews ?? 0} icon={CheckCircle2} color="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400" />
          <StatCard title={t('dashboard.stats.avgScore')} value={kpi?.avg_overall_score ? `${Math.round(kpi.avg_overall_score)}%` : "-"} icon={TrendingUp} color="bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400" onClick={() => navigate("/reports")} />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card className="card-flat lg:col-span-2">
            <CardContent className="p-5">
              <h3 className="font-display font-semibold text-ink dark:text-zinc-100 mb-4">{t('dashboard.candidatesPerJob')}</h3>
              {jobChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={jobChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[220px] flex items-center justify-center text-ink-muted text-sm">{t('dashboard.noJobsYet')}</div>
              )}
            </CardContent>
          </Card>

          <Card className="card-flat">
            <CardContent className="p-5">
              <h3 className="font-display font-semibold text-ink dark:text-zinc-100 mb-4">{t('dashboard.recommendations')}</h3>
              {pieData.length > 0 ? (
                <div className="flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={3}>
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex gap-4 mt-2">
                    {pieData.map((entry, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs text-ink-muted">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                        {entry.name} ({entry.value})
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-[220px] flex items-center justify-center text-ink-muted text-sm">{t('dashboard.noDataYet')}</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity + Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <Card className="card-flat lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-display font-semibold text-ink dark:text-zinc-100">{t('dashboard.recentInterviews')}</h3>
                <Button variant="ghost" size="sm" className="text-brand-primary dark:text-brand-accent" onClick={() => navigate("/reports")}>
                  {t('dashboard.viewAll')} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="space-y-3">
                {recentReports.length > 0 ? recentReports.map((report) => (
                  <div key={report.session_id} className="flex items-center justify-between p-3 rounded-xl bg-surface-secondary dark:bg-zinc-800/50 border border-border/50 dark:border-zinc-700/50 hover:border-border transition-colors cursor-pointer" onClick={() => navigate(`/reports/${report.session_id}`)}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        report.recommendation === 'go' ? 'bg-emerald-100 text-emerald-600' :
                        report.recommendation === 'nogo' ? 'bg-red-100 text-red-600' :
                        'bg-amber-100 text-amber-600'
                      }`}>
                        {report.recommendation === 'go' ? <CheckCircle2 className="w-4 h-4" /> :
                         report.recommendation === 'nogo' ? <AlertCircle className="w-4 h-4" /> :
                         <Clock className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="font-medium text-ink dark:text-zinc-100 text-sm">{report.candidate_name || "Candidat"}</p>
                        <p className="text-xs text-ink-muted dark:text-zinc-400">{report.job_title || ""}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {report.overall_score ? (
                        <span className={`font-bold text-sm ${
                          report.overall_score >= 70 ? 'text-emerald-600' :
                          report.overall_score >= 50 ? 'text-amber-600' : 'text-red-600'
                        }`}>{report.overall_score}%</span>
                      ) : (
                        <span className="text-xs text-ink-muted dark:text-zinc-400">{report.status}</span>
                      )}
                      <p className="text-xs text-ink-muted dark:text-zinc-500">{report.started_at ? new Date(report.started_at).toLocaleDateString() : ""}</p>
                    </div>
                  </div>
                )) : (
                  <div className="py-8 text-center text-ink-muted text-sm">{t('dashboard.noInterviewsYet')}</div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="card-flat">
            <CardContent className="p-5">
              <h3 className="font-display font-semibold text-ink dark:text-zinc-100 mb-4">{t('dashboard.quickActions')}</h3>
              <div className="space-y-3">
                <Button variant="outline" className="w-full justify-start gap-3 h-12 border-border dark:border-zinc-700 hover:bg-surface-secondary dark:hover:bg-zinc-800" onClick={() => navigate("/test-interview")} data-testid="btn-test-interview">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center"><PlayCircle className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /></div>
                  <span className="text-ink dark:text-zinc-100 text-sm font-medium">{t('dashboard.testInterview')}</span>
                </Button>
                <Button variant="outline" className="w-full justify-start gap-3 h-12 border-border dark:border-zinc-700 hover:bg-surface-secondary dark:hover:bg-zinc-800" onClick={() => navigate("/dashboard/live")} data-testid="btn-live-monitoring">
                  <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center"><Radio className="w-4 h-4 text-red-600 dark:text-red-400" /></div>
                  <span className="text-ink dark:text-zinc-100 text-sm font-medium">{t('dashboard.actions.liveMonitoring')}</span>
                </Button>
                <Button variant="outline" className="w-full justify-start gap-3 h-12 border-border dark:border-zinc-700 hover:bg-surface-secondary dark:hover:bg-zinc-800" onClick={() => navigate("/jobs")}>
                  <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center"><Briefcase className="w-4 h-4 text-blue-600 dark:text-blue-400" /></div>
                  <span className="text-ink dark:text-zinc-100 text-sm font-medium">{t('dashboard.actions.createJob')}</span>
                </Button>
                <Button variant="outline" className="w-full justify-start gap-3 h-12 border-border dark:border-zinc-700 hover:bg-surface-secondary dark:hover:bg-zinc-800" onClick={() => navigate("/interview-links")}>
                  <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center"><Link2 className="w-4 h-4 text-green-600 dark:text-green-400" /></div>
                  <span className="text-ink dark:text-zinc-100 text-sm font-medium">{t('dashboard.actions.sendLink')}</span>
                </Button>
                <Button variant="outline" className="w-full justify-start gap-3 h-12 border-border dark:border-zinc-700 hover:bg-surface-secondary dark:hover:bg-zinc-800" onClick={() => navigate("/billing")}>
                  <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center"><CreditCard className="w-4 h-4 text-amber-600 dark:text-amber-400" /></div>
                  <span className="text-ink dark:text-zinc-100 text-sm font-medium">{t('dashboard.actions.buyCredits')}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
